import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { COMPANY, NAV_LINKS, SERVICES, PARTNERSHIPS } from "../../data/site";

const DROPDOWNS = {
  "/services": SERVICES.map((s) => ({ label: s.title, to: `/services/${s.slug}` })),
  "/partnerships": PARTNERSHIPS.map((p) => ({ label: p.title, to: `/partnerships/${p.slug}` })),
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [mobileSub, setMobileSub] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMobileSub(null);
  }, [location.pathname]);

  return (
    <header className="fixed inset-x-0 top-0 z-50" data-testid="main-header">
      {/* CPL-style top utility bar */}
      <div className="bg-navy text-white" data-testid="top-utility-bar">
        <div className="mx-auto flex max-w-7xl items-center justify-end gap-4 px-6 py-1.5 lg:px-8">
          <Link
            to="/candidates#submit"
            className="text-xs font-medium tracking-wide text-white/85 transition-colors hover:text-gold"
            data-testid="utility-create-account-link"
          >
            Create an Account
          </Link>
        </div>
      </div>
      {/* Main navigation row */}
      <div
        className={`transition-all duration-300 ${
          scrolled
            ? "bg-white/90 backdrop-blur-md border-b border-border shadow-sm"
            : "bg-white/70 backdrop-blur-sm border-b border-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3 lg:px-8">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <img
            src={COMPANY.logo}
            alt={`${COMPANY.name} logo`}
            className="h-12 w-12 rounded-full object-cover ring-1 ring-border"
          />
          <div className="hidden sm:block leading-tight">
            <span className="block font-serif text-lg font-semibold text-navy">Azimi &amp; Co.</span>
            <span className="block text-[10px] tracking-[0.2em] uppercase text-gold font-semibold">
              Vision Recruitment
            </span>
          </div>
        </Link>

        <nav className="hidden items-center gap-1 xl:flex" data-testid="desktop-nav">
          {NAV_LINKS.map((link) => {
            const sub = DROPDOWNS[link.to];
            return (
              <div key={link.to} className="group relative">
                <NavLink
                  to={link.to}
                  className={({ isActive }) =>
                    `flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                      isActive ? "text-navy" : "text-charcoal/70 hover:text-navy"
                    }`
                  }
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {link.label}
                  {sub && <ChevronDown className="h-3.5 w-3.5" />}
                </NavLink>
                {sub && (
                  <div className="invisible absolute left-0 top-full w-64 translate-y-2 rounded-xl border border-border bg-white p-2 opacity-0 shadow-lg transition-all duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                    {sub.map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        className="block rounded-lg px-3 py-2 text-sm text-charcoal/75 hover:bg-secondary hover:text-navy"
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="hidden items-center gap-3 xl:flex">
          <Link
            to="/candidates#submit"
            className="rounded-lg border border-navy/20 px-4 py-2 text-sm font-semibold text-navy transition-colors hover:border-navy"
            data-testid="nav-submit-cv"
          >
            Submit CV
          </Link>
          <Link
            to="/employers#request"
            className="rounded-lg bg-gold px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-gold-dark"
            data-testid="nav-request-talent"
          >
            Request Talent
          </Link>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          className="rounded-md p-2 text-navy xl:hidden"
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="max-h-[80vh] overflow-y-auto border-t border-border bg-white px-6 py-4 xl:hidden" data-testid="mobile-nav">
          {NAV_LINKS.map((link) => {
            const sub = DROPDOWNS[link.to];
            return (
              <div key={link.to} className="border-b border-border/60 py-1">
                <div className="flex items-center justify-between">
                  <NavLink
                    to={link.to}
                    className="block flex-1 py-2 text-base font-medium text-charcoal"
                  >
                    {link.label}
                  </NavLink>
                  {sub && (
                    <button
                      onClick={() => setMobileSub(mobileSub === link.to ? null : link.to)}
                      className="p-2 text-charcoal/60"
                      aria-label="Expand"
                    >
                      <ChevronDown className={`h-4 w-4 transition-transform ${mobileSub === link.to ? "rotate-180" : ""}`} />
                    </button>
                  )}
                </div>
                {sub && mobileSub === link.to && (
                  <div className="pb-2 pl-3">
                    {sub.map((item) => (
                      <Link key={item.to} to={item.to} className="block py-2 text-sm text-charcoal/70">
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          <div className="mt-4 flex flex-col gap-3">
            <Link to="/candidates#submit" className="rounded-lg border border-navy/20 px-4 py-2.5 text-center text-sm font-semibold text-navy">
              Submit CV
            </Link>
            <Link to="/employers#request" className="rounded-lg bg-gold px-4 py-2.5 text-center text-sm font-semibold text-white">
              Request Talent
            </Link>
          </div>
        </div>
      )}
      </div>
    </header>
  );
};

export default Navbar;
